import { EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicAgendaRepository } from '../../services/eoRepositories';
import type { PublicAgendaSnapshot } from '@nx/domain';
import '@nx/ui/eo';

export function PublicAgendaPage() {
  const { t } = useI18n();
  const { token = '' } = useParams();
  const [snapshot, setSnapshot] = useState<PublicAgendaSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [dayId, setDayId] = useState('d1');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await publicAgendaRepository.getByToken(token);
      if (!cancelled) {
        setSnapshot(data);
        if (data?.days[0]) setDayId(data.days[0].id);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="eo-main" style={{ maxWidth: 900, margin: '40px auto' }}>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="eo-main" style={{ maxWidth: 900, margin: '40px auto' }}>
        <div className="empty">
          <h3>{t('eo.public.expiredTitle')}</h3>
          <p>{t('eo.public.expiredBody')}</p>
        </div>
      </div>
    );
  }

  const day = snapshot.days.find((d) => d.id === dayId) ?? snapshot.days[0];
  const activities = (snapshot.activitiesByDay[day?.date ?? ''] ?? []).sort((a, b) =>
    a.startTime.localeCompare(b.startTime),
  );

  return (
    <div className="eo-shell" style={{ gridTemplateColumns: '1fr' }}>
      <main className="eo-main" style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="public-shell">
          <div className="public-hero" style={{ marginBottom: 18 }}>
            <p className="eyebrow" style={{ color: 'var(--primary)' }}>
              N-Excelsior · {t('eo.public.eyebrow')}
            </p>
            <h2 style={{ margin: '6px 0 6px', fontFamily: 'var(--font-sans)', fontSize: 30, fontWeight: 700 }}>
              {snapshot.event.title}
            </h2>
            <p className="muted-text" style={{ margin: 0, fontSize: 14 }}>
              <EOIcon n="pin" size={14} /> {snapshot.event.location} · {snapshot.event.startDate} —{' '}
              {snapshot.event.endDate}
            </p>
          </div>

          <div className="day-tabs">
            {snapshot.days.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`day-tab ${d.id === dayId ? 'on' : ''}`}
                onClick={() => setDayId(d.id)}
              >
                <div>
                  <div className="num">{d.label}</div>
                  <div className="date">
                    {d.weekday} · {d.date.slice(5)}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div>
            {activities.map((a) => (
              <div key={a.id} className="public-activity">
                <div className="t">
                  {a.startTime}
                  <small>↓ {a.endTime}</small>
                </div>
                <div>
                  <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 15 }}>{a.title}</strong>
                  <div
                    className="muted-text"
                    style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}
                  >
                    <span>
                      <EOIcon n="pin" size={12} /> {a.venue}
                    </span>
                    <span className={`badge ${a.typeKind}`}>{a.type}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="muted-text" style={{ textAlign: 'center', fontSize: 11, marginTop: 24 }}>
            {t('eo.public.footer')}
          </p>
        </div>
      </main>
    </div>
  );
}
